import React, { useState } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { GET_EXPIRING_INVENTORY, GET_ORDERS_BY_PURCHASE_BATCH } from "./graphql";

function formatDate(value) {
  if (!value) return "—";
  const num = Number(value);
  const date = Number.isNaN(num) ? new Date(value) : new Date(num);
  return date.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
}

const InventoryTraceability = () => {
  const [withinDays, setWithinDays] = useState(30);
  const [purchaseId, setPurchaseId] = useState("");

  const { data: expiryData, loading: expiryLoading, error: expiryError } = useQuery(
    GET_EXPIRING_INVENTORY,
    { variables: { withinDays }, fetchPolicy: "network-only" },
  );

  const [fetchBatch, { data: batchData, loading: batchLoading, called: batchCalled }] =
    useLazyQuery(GET_ORDERS_BY_PURCHASE_BATCH, { fetchPolicy: "network-only" });

  const items = expiryData?.getExpiringInventory?.items || [];
  const batchOrders = batchData?.getOrdersByPurchaseBatch?.orders || [];

  return (
    <div className="space-y-6">
      {/* EXPIRING SOON */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold text-gray-900 text-sm">Expiring / Expired Inventory</h3>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Within</label>
            <select
              value={withinDays}
              onChange={(e) => setWithinDays(Number(e.target.value))}
              className="px-2 py-1 rounded-lg border border-gray-300 bg-white text-sm"
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
        </div>

        {expiryLoading && <p className="text-sm text-gray-500">Loading…</p>}
        {expiryError && (
          <p className="text-sm text-red-600">{expiryError.message}</p>
        )}
        {!expiryLoading && !expiryError && items.length === 0 && (
          <p className="text-sm text-gray-500">
            No active inventory expiring within {withinDays} days.
          </p>
        )}
        {!expiryLoading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-2">Item</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2">Employee</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Expiry Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={item.inventoryId} className={item.isExpired ? "bg-red-50" : ""}>
                    <td className="px-4 py-2">{item.name || item.inventoryId.slice(0, 8)}</td>
                    <td className="px-4 py-2">{item.categoryName}</td>
                    <td className="px-4 py-2">{item.employeeName || "Unassigned"}</td>
                    <td className="px-4 py-2">{item.status}</td>
                    <td
                      className={`px-4 py-2 font-medium ${
                        item.isExpired ? "text-red-700" : "text-amber-700"
                      }`}
                    >
                      {formatDate(item.expiryDate)}
                      {item.isExpired ? " (expired)" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* RECALL LOOKUP */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
        <h3 className="font-semibold text-gray-900 text-sm mb-1">Recall Lookup</h3>
        <p className="text-xs text-gray-500 mb-3">
          Enter a purchase/batch ID to find every booking whose scanned product came from that
          batch — for tracing an affected batch to specific customers.
        </p>
        <div className="flex items-center gap-2 mb-3">
          <input
            type="text"
            value={purchaseId}
            onChange={(e) => setPurchaseId(e.target.value)}
            placeholder="Purchase / batch ID"
            className="flex-1 px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
          <button
            type="button"
            disabled={!purchaseId || batchLoading}
            onClick={() => fetchBatch({ variables: { purchaseId } })}
            className="px-3 py-2 rounded-xl bg-[#FBB034] text-black text-sm font-semibold disabled:opacity-50"
          >
            {batchLoading ? "Searching…" : "Search"}
          </button>
        </div>

        {batchCalled && !batchLoading && batchOrders.length === 0 && (
          <p className="text-sm text-gray-500">No orders found for this batch.</p>
        )}
        {batchOrders.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-2">Booking</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Employee</th>
                  <th className="px-4 py-2">Customer</th>
                  <th className="px-4 py-2">Phone</th>
                  <th className="px-4 py-2">Category</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {batchOrders.map((o) => (
                  <tr key={o.orderId}>
                    <td className="px-4 py-2">{o.bookingId || o.orderId.slice(0, 8)}</td>
                    <td className="px-4 py-2 text-gray-500">{formatDate(o.bookingDate)}</td>
                    <td className="px-4 py-2">{o.employeeName || "—"}</td>
                    <td className="px-4 py-2">{o.customerName || "—"}</td>
                    <td className="px-4 py-2">{o.customerPhone || "—"}</td>
                    <td className="px-4 py-2">{o.categoryName || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryTraceability;
